import { Share2, MoreHorizontal, Copy, Sparkles } from 'lucide-react'
import { selectCurrentBusiness } from 'modules/business/slices/business-entities.slice'
import { useAppSelector } from 'modules/core'
import { useCallback } from 'react'
import { useBrandKitStore } from '../store/brand-kit-store'
import {
  useActiveEditingSectionStore,
  useEditorFocusStore,
  useSectionContextStore,
} from '../store/conversation-store'
import { type PinterestPinContent } from '../types'
import type { MobileSelectedSection } from '../components/mobile-selection-types'
import { EditsApplyingBadge } from '../components/EditsApplyingBadge'
import { MobileSectionSelectButton } from '../components/MobileSectionSelectButton'
import { GeneratingMediaOverlay } from '../components/AssetMedia'
import { FeedPreviewReelFallback } from './FeedPreviewReelFallback'
import { getFeedVideoPreviewState, imageAspectStyle } from './preview-utils'
import { SectionHoverWrapper } from './SectionHoverWrapper'
import { EditableInlineText } from './EditableInlineText'
import { SectionActionBar } from './SectionActionBar'
import { CanvasMediaWithBar, useHasComposerCanvas } from '../composition/editor/ComposerCanvasMedia'
import { useCompositionLoading } from '../composition/editor/use-composition-loading'
import { useIsMobile } from 'hooks/use-mobile'
import { useComposerReadOnly } from '../components/composer-read-only'
import { useCreativeStore } from '../store/creative-store'

const SKEL = 'rgba(15, 23, 42, 0.06)'
const SKEL_BG =
  'linear-gradient(110deg, rgba(15,23,42,0.03) 8%, rgba(15,23,42,0.07) 18%, rgba(15,23,42,0.03) 33%)'

interface PinterestPinPreviewProps {
  content: PinterestPinContent
  generatingFields?: Set<string>
  creativeId?: string
  selectedSectionId?: string | null
  onSectionSelect?: (section: MobileSelectedSection | null) => void
  interactive?: boolean
}

export function PinterestPinPreview({
  content,
  generatingFields,
  creativeId,
  selectedSectionId,
  onSectionSelect,
  interactive = true,
}: PinterestPinPreviewProps) {
  const business = useAppSelector(selectCurrentBusiness)
  const logoUrl = useBrandKitStore(s => s.brandKit.logoId)
  const businessName = business?.name || 'Your Business'
  const setContext = useSectionContextStore(s => s.setContext)
  const requestFocus = useEditorFocusStore(s => s.requestFocus)
  const activeField = useActiveEditingSectionStore(s =>
    creativeId ? s.activeSections[creativeId] : null
  )

  const initials = businessName
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()

  const hasReadyVideo = !!content.videoUrl
  const hasVideo = hasReadyVideo
  const hasImage = !!content.imageUrl
  const hasCanvas = useHasComposerCanvas()
  // Suppress the legacy hover while the composition is still loading, so it doesn't flash before
  // the live canvas mounts in its place.
  const compLoading = useCompositionLoading(creativeId)
  // Use the real image ratio when known (Pinterest supports it); fall back to
  // the native 2:3 pin frame only when the ratio is unknown.
  const imageStyle = imageAspectStyle(content.imageRatio)
  const hasTitle = !!content.title
  const hasDescription = !!content.description
  const isMobile = useIsMobile()
  const readOnly = useComposerReadOnly()
  const updateCreativeContent = useCreativeStore(s => s.updateCreativeContent)
  const editable = !!creativeId && !readOnly && !isMobile

  const genImage = generatingFields?.has('image') ?? false
  const genTitle = generatingFields?.has('title') ?? false
  const genDescription = generatingFields?.has('description') ?? false
  const isGenerating = (generatingFields?.size ?? 0) > 0
  const { isGeneratingVideo, isFailedReel, isVideoMode, showCardOverlay } =
    getFeedVideoPreviewState(content, isGenerating, content.imageUrl)

  const isEmpty = !hasImage && !hasVideo && !hasTitle && !hasDescription

  const handleSectionSelect = useCallback(
    (sectionType: string, label: string, preview: string) => {
      if (!onSectionSelect || !creativeId) return
      if (selectedSectionId === sectionType) {
        onSectionSelect(null)
      } else {
        onSectionSelect({
          sectionId: sectionType,
          sectionType,
          creativeType: 'pinterest_pin',
          label,
          preview,
        })
      }
    },
    [creativeId, selectedSectionId, onSectionSelect]
  )

  const handleAskAI = useCallback(
    (field: string, label: string, preview: string) => {
      if (creativeId) {
        setContext(creativeId, { sectionId: field, sectionType: field, label, preview })
      }
    },
    [creativeId, setContext]
  )

  const handleEdit = useCallback(
    (field: string) => {
      if (creativeId) {
        requestFocus(creativeId, field)
      }
    },
    [creativeId, requestFocus]
  )

  if (isEmpty && !isGenerating) {
    return (
      <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6">
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-gray-400">
          <span className="text-4xl">📌</span>
          <p className="text-sm">Your Pinterest pin will appear here</p>
        </div>
      </div>
    )
  }

  // Reels render the media at a fixed 9:16 / 320px phone-frame. Shrink the
  // card to match so the header avatar/name don't sit left of the video
  // edge — see the matching note in InstagramPostPreview.

  return (
    <div
      className={`relative mx-auto w-full overflow-hidden rounded-2xl bg-white transition-all duration-300 ${isVideoMode ? 'max-w-[320px]' : 'max-w-sm'}`}
      style={{
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {showCardOverlay && (
        <GeneratingMediaOverlay shape={isVideoMode ? 'video' : 'post'} tone="light" radius={16} />
      )}
      {/* Media — 9:16 phone-frame for video Pins, 2:3 native Pin for images */}
      {hasCanvas && hasImage && !isVideoMode && !hasVideo ? (
        <CanvasMediaWithBar
          clipClassName="relative w-full overflow-hidden"
          interactive={interactive}
          onTap={() => handleSectionSelect('image', 'Image', 'Pin image')}
        />
      ) : (
        <div
          className={`relative cursor-pointer overflow-hidden transition-all duration-200 md:cursor-default ${
            isVideoMode
              ? 'aspect-[9/16] w-full bg-black'
              : `${imageStyle ? '' : 'aspect-[2/3]'} w-full`
          } ${selectedSectionId === 'image' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
          style={isVideoMode ? undefined : imageStyle}
          onClick={() => onSectionSelect && handleSectionSelect('image', 'Image', 'Pin image')}
        >
          {hasVideo ? (
            <video
              src={content.videoUrl ?? undefined}
              controls
              playsInline
              className="h-full w-full object-cover"
            />
          ) : isFailedReel || (isGeneratingVideo && content.imageUrl) ? (
            <FeedPreviewReelFallback
              posterUrl={content.imageUrl}
              isFailedReel={isFailedReel}
              isGeneratingVideo={isGeneratingVideo}
            />
          ) : hasImage ? (
            <>
              {!genImage && creativeId && !compLoading ? (
                <SectionHoverWrapper
                  fieldId="image"
                  onAskAI={() => handleAskAI('image', 'Image', 'Pin image')}
                  onEdit={() => handleEdit('image')}
                  isActiveEditing={activeField === 'image'}
                >
                  <img
                    src={content.imageUrl ?? undefined}
                    alt={content.altText || 'Pin image'}
                    className="h-full w-full object-cover"
                  />
                </SectionHoverWrapper>
              ) : (
                <img
                  src={content.imageUrl ?? undefined}
                  alt={content.altText || 'Pin image'}
                  className="h-full w-full object-cover"
                />
              )}
              {!genImage && onSectionSelect && interactive && (
                <MobileSectionSelectButton
                  selected={selectedSectionId === 'image'}
                  onSelect={() => handleSectionSelect('image', 'Image', 'Pin image')}
                />
              )}
            </>
          ) : genImage ? (
            <div
              className="flex h-full w-full items-center justify-center"
              style={{ background: SKEL_BG }}
            >
              <div
                className="z-[1] flex h-16 w-16 items-center justify-center rounded-2xl"
                style={{ background: SKEL }}
              >
                <span className="text-2xl">🎨</span>
              </div>
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-100">
              <span className="text-4xl text-gray-300">📌</span>
            </div>
          )}
          {/* Master-image fallback only (never the live canvas branch above):
            the rendered master lags the user's composition edits until the
            server re-renders it. Video branches excluded (flag never set). */}
          {content.compositionPendingRender &&
            !isGenerating &&
            !isVideoMode &&
            !hasVideo &&
            hasImage && <EditsApplyingBadge overlay creativeId={creativeId} />}
        </div>
      )}

      {/* Content area */}
      <div className="px-4 py-3">
        {/* Action icons row */}
        <div className="mb-2 flex items-center justify-end gap-2">
          <button className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100">
            <Share2 size={16} />
          </button>
          <button className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100">
            <MoreHorizontal size={16} />
          </button>
          <button className="ml-auto rounded-full bg-[#E60023] px-4 py-1.5 text-xs font-semibold text-white">
            Save
          </button>
        </div>

        {/* Title — inline-editable on desktop; mobile keeps its tap→sheet flow. */}
        {editable && !genTitle ? (
          <SectionActionBar
            actions={[
              {
                icon: <Copy size={12} />,
                label: 'Copy',
                confirmLabel: 'Copied',
                onClick: () =>
                  navigator.clipboard
                    .writeText(content.title ?? '')
                    .catch(err => console.error('copy title failed:', err)),
              },
              {
                icon: <Sparkles size={12} />,
                label: 'Ask Taka',
                onClick: () => handleAskAI('title', 'Title', (content.title ?? '').slice(0, 80)),
              },
            ]}
          >
            <div className="mb-1">
              <EditableInlineText
                initialValue={content.title ?? ''}
                onCommit={t => updateCreativeContent(creativeId!, { title: t })}
                ariaLabel="Edit pin title"
                placeholder="Add a title…"
                className="block text-base font-semibold text-gray-900 outline-none focus:outline-none"
              />
            </div>
          </SectionActionBar>
        ) : hasTitle ? (
          <div
            className={`relative cursor-pointer transition-all duration-200 md:cursor-default ${selectedSectionId === 'title' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
            onClick={() =>
              onSectionSelect && handleSectionSelect('title', 'Title', content.title.slice(0, 80))
            }
          >
            <div className="relative mb-1 overflow-hidden">
              {activeField === 'title' && (
                <div className="pointer-events-none absolute inset-0 z-[5] bg-[color:var(--acid)]/10 transition-opacity duration-300" />
              )}
              <h3 className="text-base font-semibold text-gray-900">{content.title}</h3>
            </div>
            {!genTitle && onSectionSelect && interactive && (
              <MobileSectionSelectButton
                selected={selectedSectionId === 'title'}
                onSelect={() => handleSectionSelect('title', 'Title', content.title.slice(0, 80))}
              />
            )}
          </div>
        ) : genTitle ? (
          <div className="relative mb-1 overflow-hidden">
            <div className="h-4 w-3/4 rounded-md" style={{ background: SKEL }} />
          </div>
        ) : null}

        {/* Description — inline-editable on desktop; mobile keeps its tap→sheet flow. */}
        {editable && !genDescription ? (
          <SectionActionBar
            actions={[
              {
                icon: <Copy size={12} />,
                label: 'Copy',
                confirmLabel: 'Copied',
                onClick: () =>
                  navigator.clipboard
                    .writeText(content.description ?? '')
                    .catch(err => console.error('copy description failed:', err)),
              },
              {
                icon: <Sparkles size={12} />,
                label: 'Ask Taka',
                onClick: () =>
                  handleAskAI(
                    'description',
                    'Description',
                    (content.description ?? '').slice(0, 80)
                  ),
              },
            ]}
          >
            <div className="mb-2">
              <EditableInlineText
                initialValue={content.description ?? ''}
                onCommit={t => updateCreativeContent(creativeId!, { description: t })}
                ariaLabel="Edit pin description"
                placeholder="Add a description…"
                className="block text-sm whitespace-pre-line text-gray-600 outline-none focus:outline-none"
              />
            </div>
          </SectionActionBar>
        ) : hasDescription ? (
          <div
            className={`relative cursor-pointer transition-all duration-200 md:cursor-default ${selectedSectionId === 'description' ? 'ring-2 ring-[color:var(--acid)]/30 ring-inset' : ''}`}
            onClick={() =>
              onSectionSelect &&
              handleSectionSelect('description', 'Description', content.description.slice(0, 80))
            }
          >
            <div className="relative mb-2 overflow-hidden">
              {activeField === 'description' && (
                <div className="pointer-events-none absolute inset-0 z-[5] bg-[color:var(--acid)]/10 transition-opacity duration-300" />
              )}
              <p className="line-clamp-3 text-sm text-gray-600">{content.description}</p>
            </div>
            {!genDescription && onSectionSelect && interactive && (
              <MobileSectionSelectButton
                selected={selectedSectionId === 'description'}
                onSelect={() =>
                  handleSectionSelect(
                    'description',
                    'Description',
                    content.description.slice(0, 80)
                  )
                }
              />
            )}
          </div>
        ) : genDescription ? (
          <div className="relative mb-2 overflow-hidden">
            <div className="flex flex-col gap-2">
              <div className="h-2.5 w-full rounded-md" style={{ background: SKEL }} />
              <div className="h-2.5 w-2/3 rounded-md" style={{ background: SKEL }} />
            </div>
          </div>
        ) : null}

        {/* Link */}
        {content.link && <p className="mb-2 truncate text-xs text-gray-400">{content.link}</p>}

        {/* Author */}
        <div className="flex items-center gap-2 pt-1">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={businessName}
              className="h-8 w-8 flex-shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-[10px] font-bold text-gray-500">
              {initials}
            </div>
          )}
          <span className="text-xs font-semibold text-gray-900">{businessName}</span>
        </div>
      </div>
    </div>
  )
}
